import React from "react";
import PropTypes from "prop-types";
import { Select, Label, useId, makeStyles } from "@fluentui/react-components";
import { useIntl } from "react-intl";
import { AAModes } from "../../constants";
import { AVAILABLE_LOCALES } from "../../assets/locales/locale_config";
import { themes } from "../../utils/theme";

const useStyles = makeStyles({
  labelDisabled: {
    color: "var(--colorNeutralBackgroundInvertedDisabled)"
  }
});

function SelectWithLabel({ labelId, defaultLabel, options, value, onChange, selectId, disabled }) {
  const intl = useIntl();
  const id = useId(selectId);
  const styles = useStyles();

  return (
    <>
      <Label htmlFor={id} className={disabled ? styles.labelDisabled : undefined}>
        {intl.formatMessage({
          id: labelId,
          defaultMessage: defaultLabel
        })}
      </Label>
      <Select value={value} onChange={onChange} id={id} disabled={disabled}>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {intl.formatMessage(option.label)}
          </option>
        ))}
      </Select>
    </>
  );
}

SelectWithLabel.propTypes = {
  labelId: PropTypes.string.isRequired,
  defaultLabel: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.shape({
        id: PropTypes.string.isRequired,
        defaultMessage: PropTypes.string.isRequired
      }).isRequired
    })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  selectId: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

SelectWithLabel.defaultProps = {
  disabled: false
};

export function PanningQualitySelect({ panningQuality, onPanningQualityChange }) {
  const options = [
    { value: "Low", label: { id: "preferences-screen.audio-panning-quality.low", defaultMessage: "Low" } },
    { value: "High", label: { id: "preferences-screen.audio-panning-quality.high", defaultMessage: "High" } }
  ];

  return (
    <SelectWithLabel
      labelId="room-settings-sidebar.panning-quality"
      defaultLabel="Panning Quality"
      options={options}
      value={panningQuality}
      onChange={onPanningQualityChange}
      selectId="panning-quality"
    />
  );
}

PanningQualitySelect.propTypes = {
  panningQuality: PropTypes.string.isRequired,
  onPanningQualityChange: PropTypes.func.isRequired
};

export function VideoSendResolutionSelect({ sendResolution, onSendResolutionChange }) {
  const options = [
    { value: "180p", label: { id: "room-settings-sidebar.180p", defaultMessage: "Low Definition (180p)" } },
    { value: "360p", label: { id: "room-settings-sidebar.360p", defaultMessage: "Standard Definition (360p)" } },
    { value: "720p", label: { id: "room-settings-sidebar.720p", defaultMessage: "High Definition (720p)" } },
    { value: "1080p", label: { id: "room-settings-sidebar.1080p", defaultMessage: "Full High Definition (1080p)" } }
  ];

  return (
    <SelectWithLabel
      labelId="room-settings-sidebar.video-send-resolution"
      defaultLabel="Send Resolution"
      options={options}
      value={sendResolution}
      onChange={onSendResolutionChange}
      selectId="video-send-resolution"
    />
  );
}

VideoSendResolutionSelect.propTypes = {
  sendResolution: PropTypes.string.isRequired,
  onSendResolutionChange: PropTypes.func.isRequired
};

export function VideoReceiveResolutionSelect({ receiveResolution, onReceiveResolutionChange }) {
  const options = [
    { value: "180p", label: { id: "room-settings-sidebar.180p", defaultMessage: "Low Definition (180p)" } },
    { value: "360p", label: { id: "room-settings-sidebar.360p", defaultMessage: "Standard Definition (360p)" } },
    { value: "720p", label: { id: "room-settings-sidebar.720p", defaultMessage: "High Definition (720p)" } },
    { value: "1080p", label: { id: "room-settings-sidebar.1080p", defaultMessage: "Full High Definition (1080p)" } }
  ];

  return (
    <SelectWithLabel
      labelId="room-settings-sidebar.video-receive-resolution"
      defaultLabel="Receive Resolution"
      options={options}
      value={receiveResolution}
      onChange={onReceiveResolutionChange}
      selectId="video-receive-resolution"
    />
  );
}

VideoReceiveResolutionSelect.propTypes = {
  receiveResolution: PropTypes.string.isRequired,
  onReceiveResolutionChange: PropTypes.func.isRequired
};

export function MaterialQualitySelect({ materialQualitySetting, onMaterialQualityChange }) {
  const options = [
    { value: "low", label: { id: "preferences-screen.material-quality-setting.low", defaultMessage: "Low" } },
    { value: "medium", label: { id: "preferences-screen.material-quality-setting.medium", defaultMessage: "Medium" } },
    { value: "high", label: { id: "preferences-screen.material-quality-setting.high", defaultMessage: "High" } }
  ];

  return (
    <SelectWithLabel
      labelId="room-settings-sidebar.material-quality-setting"
      defaultLabel="Material quality"
      options={options}
      value={materialQualitySetting}
      onChange={onMaterialQualityChange}
      selectId="material-quality-setting"
    />
  );
}

MaterialQualitySelect.propTypes = {
  materialQualitySetting: PropTypes.string.isRequired,
  onMaterialQualityChange: PropTypes.func.isRequired
};

export function AntiAliasingModeSelect({ aaMode, onAAModeChange, disabled }) {
  const options = [
    { value: AAModes.NONE, label: { id: "preferences-screen.aa-mode.none", defaultMessage: "None" } },
    { value: AAModes.SMAA, label: { id: "preferences-screen.aa-mode.smaa", defaultMessage: "SMAA" } },
    { value: AAModes.MSAA_2X, label: { id: "preferences-screen.aa-mode.msaa_2x", defaultMessage: "2x MSAA" } },
    { value: AAModes.MSAA_4X, label: { id: "preferences-screen.aa-mode.msaa_4x", defaultMessage: "4x MSAA" } },
    { value: AAModes.MSAA_8X, label: { id: "preferences-screen.aa-mode.msaa_8x", defaultMessage: "8x MSAA" } }
  ];

  return (
    <SelectWithLabel
      labelId="preferences-screen.preference.fx-aa-mode"
      defaultLabel="Anti-Aliasing Mode"
      options={options}
      value={aaMode}
      onChange={onAAModeChange}
      selectId="aa-mode"
      disabled={disabled}
    />
  );
}

AntiAliasingModeSelect.propTypes = {
  aaMode: PropTypes.string.isRequired,
  onAAModeChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export function LocaleSelect({ locale, onLocaleChange }) {
  const availableLocales = [
    {
      value: "browser",
      label: { id: "preferences-screen.preference.locale.browser", defaultMessage: "Browser Default" }
    }
  ];

  for (const locale in AVAILABLE_LOCALES) {
    availableLocales.push({
      value: locale,
      label: {
        id: "preferences-screen.preference.locale." + locale,
        defaultMessage: AVAILABLE_LOCALES[locale]
      }
    });
  }

  return (
    <SelectWithLabel
      labelId="preferences-screen.preference.locale"
      defaultLabel="Language"
      options={availableLocales}
      value={locale}
      onChange={onLocaleChange}
      selectId="locale"
    />
  );
}

LocaleSelect.propTypes = {
  locale: PropTypes.string.isRequired,
  onLocaleChange: PropTypes.func.isRequired
};

export function ThemeSelect({ theme, onThemeChange }) {
  const availableThemes = [
    {
      value: "",
      label: { id: "preferences-screen.preference.theme.browser", defaultMessage: "Browser Default" }
    }
  ];

  for (const { id, name } of themes) {
    availableThemes.push({
      value: id,
      label: {
        id: "preferences-screen.preference.theme." + id,
        defaultMessage: name
      }
    });
  }

  return (
    <SelectWithLabel
      labelId="preferences-screen.preference.theme"
      defaultLabel="Theme"
      options={availableThemes}
      value={theme || ""}
      onChange={onThemeChange}
      selectId="theme"
    />
  );
}

ThemeSelect.propTypes = {
  theme: PropTypes.string,
  onThemeChange: PropTypes.func.isRequired
};

export function NametagVisibilitySelect({ nametagVisibility, onNametagVisibilityChange }) {
  const options = [
    { value: "showAll", label: { id: "preferences-screen.nametag-visibility.show-all", defaultMessage: "Always" } },
    { value: "showNone", label: { id: "preferences-screen.nametag-visibility.show-none", defaultMessage: "Never" } },
    {
      value: "showSpeaking",
      label: { id: "preferences-screen.nametag-visibility.show-speaking", defaultMessage: "Only speaking" }
    },
    {
      value: "showClose",
      label: { id: "preferences-screen.nametag-visibility.show-close", defaultMessage: "Close to me" }
    }
  ];

  return (
    <SelectWithLabel
      labelId="preferences-screen.preference.nametag-visibility"
      defaultLabel="Nametag visibility"
      options={options}
      value={nametagVisibility}
      onChange={onNametagVisibilityChange}
      selectId="nametag-visibility"
    />
  );
}

NametagVisibilitySelect.propTypes = {
  nametagVisibility: PropTypes.string.isRequired,
  onNametagVisibilityChange: PropTypes.func.isRequired
};
